import { getGaMeasurementId } from "~/lib/env";
import { getRouteVisitId } from "~/lib/route-visit";
import { registerTelemetryProvider, trackTelemetryEvent } from "../client";
import type {
  SanitizedTelemetryProperties,
  TelemetryEventName,
  TelemetryProvider,
  TelemetryScalar,
} from "../events";

type GtagValue = string | Date | Record<string, TelemetryScalar> | undefined;
type GtagFunction = (
  command: string,
  target: GtagValue,
  params?: GtagValue,
) => void;
type GaWindow = Window & {
  dataLayer?: GtagValue[][];
  gtag?: GtagFunction;
};

let gaInitializedMeasurementId: string | null = null;
let hasTrackedGaEnabled = false;

const GA4_EVENT_NAMES = {
  "agent.onboarding.stage.reached": "agent_onboarding_stage_reached",
  "agent.portfolio_construction.stage.reached": "agent_portfolio_stage_reached",
  "direct_index_builder.analysis.completed": "direct_index_analysis_completed",
  "direct_index_builder.analysis.requested": "direct_index_analysis_requested",
  "direct_index_builder.checkout.opened": "direct_index_checkout_opened",
  "direct_index_builder.walkthrough": "direct_index_builder_walkthrough",
  "execution.withdrawal.accepted": "execution_withdrawal_accepted",
  "execution.withdrawal.failed": "execution_withdrawal_failed",
  "execution.withdrawal.requested": "execution_withdrawal_requested",
  "frontend.auth.action.failed": "frontend_auth_action_failed",
  "frontend.auth.action.requested": "frontend_auth_action_requested",
  "frontend.auth.state.changed": "frontend_auth_state_changed",
  "frontend.command_palette.opened": "frontend_command_palette_opened",
  "frontend.command_palette.result.selected":
    "frontend_command_palette_result_selected",
  "frontend.notification_center.opened": "frontend_notification_center_opened",
  "frontend.route.viewed": "frontend_route_viewed",
  "frontend.user_action.failed": "frontend_user_action_failed",
  "frontend.user_action.started": "frontend_user_action_started",
  "frontend.user_action.succeeded": "frontend_user_action_succeeded",
  "platform_api.request.failed": "platform_api_request_failed",
  "telemetry.provider.enabled": "telemetry_provider_enabled",
} as const satisfies Readonly<Record<TelemetryEventName, string>>;

export const toGa4EventName = (eventName: TelemetryEventName): string =>
  GA4_EVENT_NAMES[eventName];

function getGaWindow(): GaWindow | null {
  if (typeof window === "undefined") {
    return null;
  }

  return window;
}

function ensureGtagScript(measurementId: string): void {
  if (typeof document === "undefined") {
    return;
  }

  const scriptId = "webapp-v2-ga4-gtag-src";
  if (document.getElementById(scriptId)) {
    return;
  }

  const script = document.createElement("script");
  script.async = true;
  script.id = scriptId;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;
  document.head.appendChild(script);
}

export function createGa4TelemetryProvider(
  gtag: GtagFunction,
  measurementId: string,
): TelemetryProvider {
  const resetIdentity = (): void => {
    gtag("config", measurementId, {
      send_page_view: false,
      user_id: null,
    });
    gtag("set", "user_properties", {
      authenticated: false,
      auth_source: "guest",
    });
  };

  return {
    name: "ga4",
    identify(identity) {
      if (!identity.authenticated || !identity.canonicalUserId) {
        resetIdentity();
        return;
      }

      gtag("config", measurementId, {
        send_page_view: false,
        user_id: identity.canonicalUserId,
      });
      gtag("set", "user_properties", {
        authenticated: true,
        auth_source: identity.authSource,
      });
    },
    reset: resetIdentity,
    track(event) {
      if (event.name === "frontend.route.viewed") {
        gtag("event", "page_view", {
          page_path: getStringProperty(event.properties, "route_path") ?? "/",
          route_visit_id:
            getStringProperty(event.properties, "route_visit_id") ?? null,
          route_id: getStringProperty(event.properties, "route_id") ?? null,
        });
        return;
      }

      gtag(
        "event",
        toGa4EventName(event.name),
        toGaEventProperties(event.properties),
      );
    },
  };
}

function getStringProperty(
  properties: SanitizedTelemetryProperties,
  key: string,
): string | null {
  const value = properties[key];
  return typeof value === "string" ? value : null;
}

function toGaEventProperties(
  properties: SanitizedTelemetryProperties,
): Record<string, TelemetryScalar> {
  const gaProperties: Record<string, TelemetryScalar> = {};
  for (const [key, value] of Object.entries(properties)) {
    gaProperties[key] = value;
  }

  return gaProperties;
}

export function enableGa4Telemetry(): void {
  const measurementId = getGaMeasurementId();
  if (!measurementId || gaInitializedMeasurementId === measurementId) {
    return;
  }

  const gaWindow = getGaWindow();
  if (!gaWindow) {
    return;
  }

  gaWindow.dataLayer ??= [];
  gaWindow.gtag ??= (command, target, params) => {
    gaWindow.dataLayer?.push([command, target, params]);
  };

  ensureGtagScript(measurementId);
  gaWindow.gtag("js", new Date());
  gaWindow.gtag("config", measurementId, {
    send_page_view: false,
  });
  registerTelemetryProvider(
    createGa4TelemetryProvider(gaWindow.gtag, measurementId),
  );

  gaInitializedMeasurementId = measurementId;
  if (!hasTrackedGaEnabled) {
    hasTrackedGaEnabled = true;
    trackTelemetryEvent("telemetry.provider.enabled", {
      provider: "ga4",
      route_visit_id: getRouteVisitId(),
      session_replay_enabled: false,
    });
  }
}

export function resetGa4TelemetryForTests(): void {
  gaInitializedMeasurementId = null;
  hasTrackedGaEnabled = false;
}
